import { useEffect, useRef } from 'react'
import { Renderer, Program, Mesh, Color, Triangle } from 'ogl'
import './css/Iridescence.css'

const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`

const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`

export default function Iridescence({
  color = [1, 1, 1],
  speed = 1.0,
  amplitude = 0.1,
  mouseReact = true,
  className = '',
  ...rest
}) {
  const containerRef = useRef(null)
  const mousePos = useRef({ x: 0.5, y: 0.5 })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    let renderer
    let gl

    try {
      renderer = new Renderer()
      ;({ gl } = renderer)
    } catch (error) {
      console.error('Iridescence failed to initialize WebGL', error)
      return undefined
    }
    gl.clearColor(1, 1, 1, 1)

    let program = null

    const resize = () => {
      const parentRect = container.parentElement?.getBoundingClientRect()
      const width = parentRect?.width || container.offsetWidth || 1
      const height = parentRect?.height || container.offsetHeight || 1
      const scale = window.devicePixelRatio > 1 ? 1.5 : 1

      renderer.setSize(width * scale, height * scale)
      gl.canvas.style.width = `${width}px`
      gl.canvas.style.height = `${height}px`

      if (program) {
        program.uniforms.uResolution.value = new Color(
          gl.canvas.width,
          gl.canvas.height,
          gl.canvas.width / gl.canvas.height
        )
      }
    }

    window.addEventListener('resize', resize)
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
    if (resizeObserver) {
      resizeObserver.observe(container.parentElement || container)
    }
    resize()

    const geometry = new Triangle(gl)
    program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new Color(...color) },
        uResolution: {
          value: new Color(
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height
          )
        },
        uMouse: { value: new Float32Array([mousePos.current.x, mousePos.current.y]) },
        uAmplitude: { value: amplitude },
        uSpeed: { value: speed }
      }
    })

    const mesh = new Mesh(gl, { geometry, program })
    let animationId = 0

    const update = time => {
      animationId = requestAnimationFrame(update)
      program.uniforms.uTime.value = time * 0.001
      renderer.render({ scene: mesh })
    }
    animationId = requestAnimationFrame(update)

    container.appendChild(gl.canvas)

    const handleMouseMove = event => {
      const rect = container.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) || 0.5
      const y = 1.0 - ((event.clientY - rect.top) / rect.height || 0.5)
      mousePos.current = { x, y }
      program.uniforms.uMouse.value[0] = x
      program.uniforms.uMouse.value[1] = y
    }

    if (mouseReact) {
      window.addEventListener('mousemove', handleMouseMove)
    }

    return () => {
      cancelAnimationFrame(animationId)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      window.removeEventListener('resize', resize)
      if (mouseReact) {
        window.removeEventListener('mousemove', handleMouseMove)
      }
      if (gl.canvas.parentNode === container) {
        container.removeChild(gl.canvas)
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }, [color, speed, amplitude, mouseReact])

  const mergedClassName = ['iridescence-container', className].filter(Boolean).join(' ')

  return <div ref={containerRef} className={mergedClassName} {...rest} />
}
